/**
 * The Email to Professor Generator: eight reasons to write, two tones, and the fields
 * each reason needs.
 *
 * The bank below is the whole of the generated English. It is spread into the widget's
 * `EMAIL_TO_PROFESSOR_GENERATOR_STRINGS`, so a translated content file can override any
 * frame through `ui` exactly as it overrides a button label. Frame syntax (`?field:`,
 * `!field:`, `¶`) is documented in `compose.ts`.
 */

import { t } from "@/lib/i18n/interpolate";
import {
  addressName,
  asFragment,
  asSentence,
  cleanLine,
  compose,
  draftToPlain,
  fillFrames,
  orPlaceholder,
  placeholder,
  stripHonorific,
} from "@/lib/tool-kit/correspondence/compose";

export const PROFESSOR_EMAIL_PURPOSES = [
  "extension",
  "absence",
  "question",
  "meeting",
  "recommendation",
  "grade",
  "research",
  "introduction",
] as const;

export type ProfessorEmailPurpose = (typeof PROFESSOR_EMAIL_PURPOSES)[number];

export const PROFESSOR_TITLES = [
  "professor",
  "dr",
  "drNoStop",
  "ms",
  "mr",
  "mx",
] as const;

export type ProfessorTitle = (typeof PROFESSOR_TITLES)[number];

export type EmailTone = "formal" | "friendly";

/**
 * How a detail's text is used: inside a frame's sentence, as sentences of its own, or
 * as a yes/no box that switches frames on.
 */
export type DetailShape = "fragment" | "sentence" | "checkbox";

export interface DetailSpec {
  id: string;
  shape: DetailShape;
  /** Shown as a placeholder when blank; an optional detail just drops its sentence. */
  required?: boolean;
  /** A text area rather than a one-line input. */
  multiline?: boolean;
}

/** The fields each reason asks for, in form order. Ids are unique across reasons. */
export const PROFESSOR_EMAIL_DETAILS: Readonly<
  Record<ProfessorEmailPurpose, readonly DetailSpec[]>
> = {
  extension: [
    { id: "assignment", shape: "fragment", required: true },
    { id: "dueDate", shape: "fragment", required: true },
    { id: "newDate", shape: "fragment", required: true },
    { id: "reason", shape: "fragment", required: true },
    { id: "progress", shape: "fragment" },
    { id: "documentation", shape: "checkbox" },
  ],
  absence: [
    { id: "absenceDate", shape: "fragment", required: true },
    { id: "upcoming", shape: "checkbox" },
    { id: "absenceReason", shape: "fragment" },
    { id: "catchUp", shape: "fragment" },
    { id: "missedWork", shape: "fragment" },
  ],
  question: [
    { id: "topic", shape: "fragment", required: true },
    { id: "question", shape: "sentence", required: true, multiline: true },
    { id: "tried", shape: "fragment" },
  ],
  meeting: [
    { id: "meetingTopic", shape: "fragment", required: true },
    { id: "meetingTimes", shape: "fragment", required: true },
  ],
  recommendation: [
    { id: "program", shape: "fragment", required: true },
    { id: "deadline", shape: "fragment", required: true },
    { id: "connection", shape: "sentence", required: true, multiline: true },
    { id: "why", shape: "sentence", multiline: true },
    { id: "materials", shape: "fragment" },
  ],
  grade: [
    { id: "assignment", shape: "fragment", required: true },
    { id: "grade", shape: "fragment" },
    { id: "understand", shape: "fragment", required: true },
  ],
  research: [
    { id: "theirWork", shape: "fragment", required: true },
    { id: "background", shape: "sentence", required: true, multiline: true },
    { id: "hours", shape: "fragment" },
    { id: "cv", shape: "checkbox" },
  ],
  introduction: [
    { id: "context", shape: "fragment", required: true },
    { id: "aboutYou", shape: "sentence", multiline: true },
    { id: "ask", shape: "fragment" },
  ],
};

/** Reasons that are about a course the reader is taking — the course must be named. */
export const COURSE_REQUIRED: Readonly<Record<ProfessorEmailPurpose, boolean>> =
  {
    extension: true,
    absence: true,
    question: true,
    meeting: true,
    recommendation: false,
    grade: true,
    research: false,
    introduction: false,
  };

/** Everything the email says, in English. Spread into the widget's strings. */
export const PROFESSOR_EMAIL_BANK = {
  titles: {
    professor: "Professor",
    dr: "Dr.",
    drNoStop: "Dr",
    ms: "Ms.",
    mr: "Mr.",
    mx: "Mx.",
  },
  /** How a title and a name join in the greeting; "{name} {title}" puts the title last. */
  addressFormat: "{title} {name}",
  greetings: {
    formal: "Dear {name},",
    friendly: "Hello {name},",
  },
  signOffs: {
    formal: "Kind regards,",
    friendly: "Best wishes,",
  },
  studentIdLine: "Student ID: {studentId}",
  subjectLine: "Subject: {subject}",
  placeholders: {
    recipient: "Surname",
    yourName: "Your name",
    course: "Course",
    assignment: "Assignment",
    dueDate: "Current deadline",
    newDate: "New deadline",
    reason: "Brief reason",
    absenceDate: "Class or date",
    topic: "Topic",
    question: "Your question",
    meetingTopic: "What you want to discuss",
    meetingTimes: "Times you are free",
    program: "What you are applying for",
    deadline: "Deadline",
    connection: "How they know your work",
    understand: "What you want to understand",
    theirWork: "Their research that interests you",
    background: "Your relevant background",
    context: "Why you are writing",
  },
  subjects: {
    extension: [
      "{course}: extension request for {assignment}",
      "Extension request — {assignment}, {course}",
      "{assignment} ({course}): could I have more time?",
    ],
    absence: [
      "{course}: absence from {absenceDate}",
      "Absence from {absenceDate} — {yourName}, {course}",
      "!upcoming:{course}: catching up after missing {absenceDate}",
      "?upcoming:{course}: upcoming absence from {absenceDate}",
    ],
    question: [
      "{course}: question about {topic}",
      "Question about {topic} ({course})",
      "{course} — {topic}: a quick question",
    ],
    meeting: [
      "{course}: request to meet about {meetingTopic}",
      "Meeting request — {meetingTopic} ({course})",
      "{course}: could we meet to discuss {meetingTopic}?",
    ],
    recommendation: [
      "Recommendation letter request — {program}",
      "Reference request for {program}",
      "Request for a letter of recommendation (deadline {deadline})",
    ],
    grade: [
      "{course}: question about my grade for {assignment}",
      "Feedback on {assignment} — {course}",
      "{course}: understanding my grade for {assignment}",
    ],
    research: [
      "Research assistant opportunities in your group",
      "Interest in your research — {yourName}",
      "Could I help with research in your group?",
    ],
    introduction: [
      "Introduction — {yourName}",
      "?course:Introducing myself ahead of {course}",
      "!course:Introducing myself — {yourName}",
      "A brief introduction from {yourName}",
    ],
  },
  alternatives: {
    extension: [
      "Would it be possible for me to submit {assignment} by {newDate}?",
      "I would like to request a short extension on {assignment}, to {newDate}.",
      "Could I have until {newDate} to submit {assignment}?",
    ],
    absence: [
      "!upcoming:I am sorry to have missed {absenceDate}, and I would be grateful for your advice on catching up.",
      "?upcoming:I wanted to let you know ahead of time that I will miss {absenceDate}.",
      "What would be the best way for me to catch up on {absenceDate}?",
      "?missedWork:Would it be possible to arrange a make-up for {missedWork}?",
    ],
    question: [
      "Could you help me understand {topic}?",
      "I would appreciate your help with one point in {topic}.",
      "I am having trouble with one part of {topic}, and I wondered if you could clarify it.",
    ],
    meeting: [
      "Would you have time for a short meeting to discuss {meetingTopic}?",
      "Could I come to your office hours to talk about {meetingTopic}?",
      "I would be grateful for a few minutes of your time to discuss {meetingTopic}.",
    ],
    recommendation: [
      "Would you feel able to write a strong letter of recommendation for my application to {program}?",
      "Would you be willing to act as a referee for my application to {program}?",
      "I would be very grateful if you would consider writing a reference for me.",
    ],
    grade: [
      "Could you help me understand how my work on {assignment} could have been stronger?",
      "I would appreciate any further guidance on how to improve after {assignment}.",
      "Would you be able to go over the feedback on {assignment} with me?",
    ],
    research: [
      "Do you have any openings for research assistants in your group at the moment?",
      "Would you be open to a short conversation about research opportunities in your group?",
      "I would be glad to help with any part of your current research where an assistant would be useful.",
    ],
    introduction: [
      "I wanted to take a moment to introduce myself.",
      "I am writing to introduce myself, as {context}.",
      "I hope you do not mind me writing to introduce myself.",
    ],
  },
  formal: {
    extension: [
      "My name is {yourName}, and I am a student in your {course} course.",
      "I am writing to ask whether I could have an extension on {assignment}, which is due on {dueDate}.",
      "¶",
      "Because of {reason}, I will not be able to complete it to the standard I would like by the deadline.",
      "?progress:So far, {progress}.",
      "I would be grateful for the chance to submit it by {newDate}.",
      "?documentation:I can provide supporting documentation if it is needed.",
      "¶",
      "If extensions are handled through a formal process or form, please let me know and I will follow it.",
      "Thank you for considering my request.",
    ],
    absence: [
      "My name is {yourName}, and I am a student in your {course} course.",
      "!upcoming?absenceReason:I am writing to let you know that I was unable to attend {absenceDate} because of {absenceReason}, and I am sorry to have missed it.",
      "!upcoming!absenceReason:I am writing to let you know that I was unable to attend {absenceDate}, and I am sorry to have missed it.",
      "?upcoming?absenceReason:I am writing to let you know in advance that I will not be able to attend {absenceDate} because of {absenceReason}.",
      "?upcoming!absenceReason:I am writing to let you know in advance that I will not be able to attend {absenceDate}.",
      "¶",
      "?catchUp:To keep up with the course, {catchUp}.",
      "?missedWork:Could you let me know whether it would be possible to make up {missedWork}, or whether there is anything else I should do?",
      "!missedWork:Please let me know if there is anything else I should do to catch up.",
      "¶",
      "Thank you for your understanding.",
    ],
    question: [
      "My name is {yourName}, and I am a student in your {course} course.",
      "I have a question about {topic}.",
      "¶",
      "{question}",
      "?tried:I have already {tried}, but I am still unsure.",
      "¶",
      "If it would be easier to discuss this in office hours, I would be happy to come by.",
      "Thank you for your help.",
    ],
    meeting: [
      "My name is {yourName}, and I am a student in your {course} course.",
      "I would like to meet with you to discuss {meetingTopic}.",
      "¶",
      "Would it be possible to meet during your office hours, or at another time that suits you?",
      "I am available {meetingTimes}.",
      "¶",
      "Thank you for your time.",
    ],
    recommendation: [
      "My name is {yourName}.",
      "{connection}",
      "¶",
      "I am applying for {program}, and I am writing to ask whether you would be willing to write a letter of recommendation for me.",
      "?why:{why}",
      "The deadline for letters is {deadline}.",
      "¶",
      "?materials:If you are able to help, I would be glad to send you {materials}, along with anything else that would be useful.",
      "I completely understand if you are unable to take this on.",
      "Thank you for considering my request.",
    ],
    grade: [
      "My name is {yourName}, and I am a student in your {course} course.",
      "?grade:I am writing about my grade for {assignment}, for which I received {grade}.",
      "!grade:I am writing about my grade for {assignment}.",
      "¶",
      "I have read the feedback carefully, and I would like to understand {understand}.",
      "My aim is to do better on the next assessment.",
      "¶",
      "Would it be possible to go over this briefly, either by email or in office hours?",
      "Thank you for your time.",
    ],
    research: [
      "?course:My name is {yourName}, and I am a student in your {course} course.",
      "!course:My name is {yourName}.",
      "I am writing to ask whether there might be an opportunity to assist with research in your group.",
      "¶",
      "I am particularly interested in {theirWork}.",
      "{background}",
      "¶",
      "?hours:I could commit {hours}.",
      "?cv:I have attached my CV for your reference.",
      "I would welcome the chance to discuss whether I could contribute to your work.",
      "Thank you for your time and consideration.",
    ],
    introduction: [
      "My name is {yourName}.",
      "{context}, and I wanted to introduce myself.",
      "¶",
      "?aboutYou:{aboutYou}",
      "?ask:I would also like to ask {ask}.",
      "¶",
      "Thank you for your time, and I look forward to working with you.",
    ],
  },
  friendly: {
    extension: [
      "I'm {yourName}, a student in your {course} course.",
      "I'm writing to ask if I could have an extension on {assignment}, which is due on {dueDate}.",
      "¶",
      "Because of {reason}, I won't be able to finish it to the standard I'd like by the deadline.",
      "?progress:So far, {progress}.",
      "Would it be possible for me to hand it in by {newDate} instead?",
      "?documentation:I'm happy to send supporting documentation if that would help.",
      "¶",
      "If there's a form I should fill in instead, just let me know.",
      "Thanks very much for considering it.",
    ],
    absence: [
      "I'm {yourName}, a student in your {course} course.",
      "!upcoming?absenceReason:I wanted to let you know that I couldn't make it to {absenceDate} because of {absenceReason} — I'm sorry to have missed it.",
      "!upcoming!absenceReason:I wanted to let you know that I couldn't make it to {absenceDate} — I'm sorry to have missed it.",
      "?upcoming?absenceReason:I wanted to let you know in advance that I won't be able to make it to {absenceDate} because of {absenceReason}.",
      "?upcoming!absenceReason:I wanted to let you know in advance that I won't be able to make it to {absenceDate}.",
      "¶",
      "?catchUp:To catch up, {catchUp}.",
      "?missedWork:Would it be possible to make up {missedWork}, or is there anything else I should do?",
      "!missedWork:Is there anything else I should do to catch up?",
      "¶",
      "Thanks for understanding.",
    ],
    question: [
      "I'm {yourName}, a student in your {course} course.",
      "I have a quick question about {topic}.",
      "¶",
      "{question}",
      "?tried:I've already {tried}, but I'm still not sure I've got it.",
      "¶",
      "If it's easier to go over this in office hours, I'm happy to come by.",
      "Thanks so much for your help.",
    ],
    meeting: [
      "I'm {yourName}, a student in your {course} course.",
      "I'd like to meet with you to talk about {meetingTopic}.",
      "¶",
      "Would your office hours work, or is there another time that suits you better?",
      "I'm free {meetingTimes}.",
      "¶",
      "Thanks for your time.",
    ],
    recommendation: [
      "I'm {yourName}.",
      "{connection}",
      "¶",
      "I'm applying for {program}, and I wanted to ask if you'd be willing to write me a letter of recommendation.",
      "?why:{why}",
      "Letters are due on {deadline}.",
      "¶",
      "?materials:If you can, I'll send you {materials}, plus anything else that would help.",
      "And of course, I completely understand if you can't take it on.",
      "Thank you so much for considering it.",
    ],
    grade: [
      "I'm {yourName}, a student in your {course} course.",
      "?grade:I'm writing about my grade for {assignment} — I received {grade}.",
      "!grade:I'm writing about my grade for {assignment}.",
      "¶",
      "I've gone through the feedback, and I'd like to understand {understand}.",
      "I want to make sure I do better next time.",
      "¶",
      "Could we go over it briefly, by email or in office hours?",
      "Thanks for your time.",
    ],
    research: [
      "?course:I'm {yourName}, a student in your {course} course.",
      "!course:I'm {yourName}.",
      "I'm writing to ask whether there might be a chance to help with research in your group.",
      "¶",
      "I'm especially interested in {theirWork}.",
      "{background}",
      "¶",
      "?hours:I could put in {hours}.",
      "?cv:I've attached my CV in case it's useful.",
      "I'd love to talk about whether I could contribute.",
      "Thanks for considering it.",
    ],
    introduction: [
      "I'm {yourName}.",
      "{context}, and I wanted to say hello and introduce myself.",
      "¶",
      "?aboutYou:{aboutYou}",
      "?ask:I'd also like to ask {ask}.",
      "¶",
      "Thanks for reading, and I'm looking forward to working with you.",
    ],
  },
} as const;

export interface ProfessorEmailBank {
  titles: Readonly<Record<ProfessorTitle, string>>;
  /** The title menu's labels, which may carry a note: "Доктор (Dr.)". */
  titleOptions?: Readonly<Record<ProfessorTitle, string>>;
  addressFormat?: string;
  greetings: Readonly<Record<EmailTone, string>>;
  signOffs: Readonly<Record<EmailTone, string>>;
  studentIdLine: string;
  subjectLine: string;
  placeholders: Readonly<Record<string, string>>;
  subjects: Readonly<Record<ProfessorEmailPurpose, readonly string[]>>;
  alternatives: Readonly<Record<ProfessorEmailPurpose, readonly string[]>>;
  formal: Readonly<Record<ProfessorEmailPurpose, readonly string[]>>;
  friendly: Readonly<Record<ProfessorEmailPurpose, readonly string[]>>;
}

export interface ProfessorEmailInput {
  purpose: ProfessorEmailPurpose;
  tone: EmailTone;
  title: ProfessorTitle;
  recipient: string;
  course: string;
  yourName: string;
  studentId: string;
  /** Detail values by id; a checkbox is "yes" when ticked. */
  details: Readonly<Record<string, string>>;
}

export interface ProfessorEmailDraft {
  /** Up to three subject lines; the first is the one the download uses. */
  subjects: string[];
  /** Greeting, body paragraphs and signature, with marks. */
  paragraphs: string[];
  /** Other ways to make the request, one sentence each. */
  alternatives: string[];
}

const LABEL_NOTE_BRACKET = /[()（）]/;
const NOT_A_TITLE = /[\s,，、;；]/;

/**
 * Every word the locale uses for a title: the ones put in the greeting, and those on the
 * menu. A menu label is "Доктор (Dr.)" or "Dr (no full stop)": the words before the
 * bracket count, and so does a bracketed single word, but not a bracketed explanation.
 */
export function professorTitleWords(
  bank: Pick<ProfessorEmailBank, "titles" | "titleOptions">
): string[] {
  const words = new Set<string>(Object.values(bank.titles));
  for (const label of Object.values(bank.titleOptions ?? {})) {
    const [main = "", ...notes] = label.split(LABEL_NOTE_BRACKET);
    words.add(main.trim());
    for (const note of notes) {
      if (note.trim() !== "" && !NOT_A_TITLE.test(note.trim())) {
        words.add(note.trim());
      }
    }
  }
  return [...words].filter((word) => word !== "");
}

function readDetails(
  input: ProfessorEmailInput,
  bank: ProfessorEmailBank,
  values: Record<string, string>,
  flags: Set<string>
): void {
  for (const spec of PROFESSOR_EMAIL_DETAILS[input.purpose]) {
    const raw = input.details[spec.id] ?? "";
    if (spec.shape === "checkbox") {
      if (raw === "yes") {
        flags.add(spec.id);
      }
      continue;
    }
    const value = spec.shape === "sentence" ? asSentence(raw) : asFragment(raw);
    if (value !== "") {
      values[spec.id] = value;
      flags.add(spec.id);
    } else if (spec.required) {
      values[spec.id] = placeholder(bank.placeholders[spec.id] ?? spec.id);
    }
  }
}

export function buildProfessorEmail(
  input: ProfessorEmailInput,
  bank: ProfessorEmailBank
): ProfessorEmailDraft {
  const values: Record<string, string> = {};
  const flags = new Set<string>();

  const recipient = orPlaceholder(
    stripHonorific(input.recipient, professorTitleWords(bank)),
    bank.placeholders.recipient ?? ""
  );
  values.name = addressName(
    bank.titles[input.title],
    recipient,
    bank.addressFormat
  );
  values.yourName = orPlaceholder(
    cleanLine(input.yourName),
    bank.placeholders.yourName ?? ""
  );
  const course = asFragment(input.course);
  if (course !== "") {
    values.course = course;
    flags.add("course");
  } else if (COURSE_REQUIRED[input.purpose]) {
    values.course = placeholder(bank.placeholders.course ?? "");
  }
  readDetails(input, bank, values, flags);

  const signature = [bank.signOffs[input.tone], values.yourName];
  const studentId = cleanLine(input.studentId);
  if (studentId !== "") {
    signature.push(t(bank.studentIdLine, { studentId }));
  }

  return {
    subjects: fillFrames(bank.subjects[input.purpose], values, flags).slice(
      0,
      3
    ),
    paragraphs: [
      t(bank.greetings[input.tone], { name: values.name }),
      ...compose(bank[input.tone][input.purpose], values, flags),
      signature.join("\n"),
    ],
    alternatives: fillFrames(
      bank.alternatives[input.purpose],
      values,
      flags
    ).slice(0, 3),
  };
}

/** The download: the first subject line, then the email. */
export function professorEmailText(
  draft: ProfessorEmailDraft,
  bank: Pick<ProfessorEmailBank, "subjectLine">
): string {
  const subject = draft.subjects[0];
  const body = draftToPlain(draft.paragraphs);
  if (!subject) {
    return body;
  }
  return `${t(bank.subjectLine, { subject: draftToPlain([subject]) })}\n\n${body}`;
}
